import * as React from "react"
import type { UserArea } from "@/shared/types/auth"

// Types
interface TeamContextType {
  selectedTeam: string
  setSelectedTeam: (team: string) => void
  /** Áreas dinámicas del usuario (del endpoint /auth/me) */
  userAreas: UserArea[]
  setUserAreas: (areas: UserArea[]) => void
  /** Área seleccionada actualmente (objeto completo) */
  selectedArea: UserArea | undefined
}

// Context
const TeamContext = React.createContext<TeamContextType | undefined>(undefined)

// Provider
interface TeamProviderProps {
  children: React.ReactNode
}

export function TeamProvider({ children }: TeamProviderProps) {
  const [selectedTeam, setSelectedTeamState] = React.useState<string>(() => {
    const stored = localStorage.getItem("tonal-selected-team")
    return stored || "Tonal"
  })

  const [userAreas, setUserAreas] = React.useState<UserArea[]>([])

  const setSelectedTeam = React.useCallback((team: string) => {
    setSelectedTeamState(team)
    localStorage.setItem("tonal-selected-team", team)
  }, [])

  const selectedArea = React.useMemo(
    () => userAreas.find((a) => a.nombre === selectedTeam),
    [userAreas, selectedTeam]
  )

  // Sincronizar area ID en localStorage para el interceptor de Axios
  React.useEffect(() => {
    if (selectedArea?.id) {
      localStorage.setItem('tonal-selected-area-id', String(selectedArea.id))
    } else {
      localStorage.removeItem('tonal-selected-area-id')
    }
  }, [selectedArea?.id])

  const value = React.useMemo(
    () => ({ selectedTeam, setSelectedTeam, userAreas, setUserAreas, selectedArea }),
    [selectedTeam, setSelectedTeam, userAreas, setUserAreas, selectedArea]
  )

  return <TeamContext.Provider value={value}>{children}</TeamContext.Provider>
}

// Hook
export function useTeam() {
  const context = React.useContext(TeamContext)
  if (context === undefined) {
    throw new Error("useTeam must be used within a TeamProvider")
  }
  return context
}
